import { html, CSSResultGroup } from 'lit';
import { property } from 'lit/decorators.js';
import { ifDefined } from 'lit/directives/if-defined.js';
import { BlocksElement } from '../../BlocksElement';
import styles from '../icon.styles';
import type { IconColorType, IconSize } from './icon.model';

/**
 * Email component
 * @csspart base- The component's base wrapper
 */
export class BlocksIconEmail extends BlocksElement {static styles: CSSResultGroup = styles;

  /**
   * Icon color variant
   * <cat-text-passage size="sm">
   * <ul>
   * <li>No color defined renders an icon with the currentColor of its parent element</li>
   * <li>**content-strong** renders an icon with the cat-theme-color-content-strong color token</li>
   * <li>**knockout** renders an icon with the cat-theme-color-content-knockout color token</li>
   * <li>**knockout-brand** renders a renders an icon with the cat-theme-color-content-knockout-brand color token</li>
   * <li>**information** renders an icon with the cat-theme-color-content-utility-information color token</li>
   * <li>**success** renders an icon with the cat-theme-color-content-utility-success color token</li>
   * <li>**warning** renders a renders an icon with the cat-theme-color-content-utility-warning color token</li>
   * <li>**error** renders an icon with the cat-theme-color-content-utility-error color token</li>
   * </ul>
   * </cat-text-passage>
   */
  @property({ type: String })
  color: IconColorType;

  /**
   * Title
   * 1) Icons should always have associated text. Use this to supply `aria-label` to icon if it stands by itself without any visual/visually-hidden text
   */
  @property({ type: String })
  iconTitle: string | undefined;

  /**
   * Size property
   * - Default size is 16px
   * - **sm** renders a larger size than default (12px)
   * - **lg** renders a larger size than default (24px)
   * - **xl** renders a larger size than the lg variant (32px);
   */
  @property({ type: String })
  size: IconSize;

  connectedCallback() {
    super.connectedCallback();
    this.classList.add('cat-icon');
  }

  render() {
    const componentClassName = this.componentClassNames('cat-c-icon', {
      'cat-c-icon--sm': this.size === 'sm',
      'cat-c-icon--lg': this.size === 'lg',
      'cat-c-icon--xl': this.size === 'xl',
      'cat-c-icon--content-strong': this.color === 'content-strong',
      'cat-c-icon--knockout': this.color === 'knockout',
      'cat-c-icon--knockout-brand': this.color === 'knockout-brand',
      'cat-c-icon--information': this.color === 'information',
      'cat-c-icon--success': this.color === 'success',
      'cat-c-icon--warning': this.color === 'warning',
      'cat-c-icon--error': this.color === 'error'
    });

    return html`
      <span part="base" class="${componentClassName}" role="presentation">
        <svg
          aria-hidden="${!this.iconTitle}"
          aria-label="${ifDefined(this.iconTitle)}"
          role="${this.iconTitle && 'img'}"
          width="24"
          height="24"
          viewBox="0 0 24 24"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path
            fill-rule="evenodd"
            clip-rule="evenodd"
            d="M2.60449 5.36668C2.60449 4.9189 2.96749 4.55591 3.41526 4.55591H20.5837C21.0315 4.55591 21.3945 4.9189 21.3945 5.36668V5.41089L12.5155 12.9868C12.3703 13.1066 12.1879 13.1722 11.9995 13.1722C11.8111 13.1722 11.6287 13.1066 11.4835 12.9868L2.60449 5.41086V5.36668ZM2.60449 7.38269L10.5138 14.1313L10.5206 14.137C10.9358 14.4828 11.4591 14.6722 11.9995 14.6722C12.5399 14.6722 13.0632 14.4829 13.4784 14.137L21.3945 7.3827V18.6332C21.3945 19.081 21.0315 19.444 20.5837 19.444H3.41526C2.96749 19.444 2.60449 19.081 2.60449 18.6332V7.38269ZM22.8945 5.76317V18.6332C22.8945 19.9094 21.8599 20.944 20.5837 20.944H3.41526C2.13906 20.944 1.10449 19.9094 1.10449 18.6332V5.75649V5.36668C1.10449 4.09047 2.13906 3.05591 3.41526 3.05591H20.5837C21.8599 3.05591 22.8945 4.09047 22.8945 5.36668V5.74982C22.8945 5.75427 22.8945 5.75872 22.8945 5.76317Z"
          />
        </svg>
      </span>
    `;
  }
}

if (customElements.get('cat-icon-email') === undefined) {
  customElements.define('cat-icon-email', BlocksIconEmail);
}

declare global {
  interface HTMLElementTagNameMap {
    'cat-icon-email': BlocksIconEmail;
  }
}
